<template>
  <div
    class="fixed inset-0 z-[100] flex justify-center overflow-y-auto bg-ink/50 px-4 py-6"
  >
    <div ref="dialogPanel" role="dialog" aria-modal="true" :aria-labelledby="dialogTitleId" tabindex="-1" @keydown="onDialogKeydown" class="w-full max-w-md flex flex-col paper-card rounded-md overflow-hidden my-auto">
      <div class="flex items-center gap-2 p-3.5 border-b border-[hsl(var(--border)/0.6)] shrink-0">
        <p :id="dialogTitleId" class="flex-1 min-w-0 text-[15px] font-semibold">{{ $t('feedback.form.title') }}</p>
        <button
          @click="close"
          class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
          :aria-label="$t('liveHome.close')"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <div v-if="error" class="mx-4 mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-[hsl(var(--danger)/0.08)] text-[hsl(var(--danger))] shrink-0">
        <CircleAlert class="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <p class="text-[12px] leading-snug">{{ error }}</p>
      </div>

      <div class="overflow-y-auto px-4 py-3.5 space-y-3.5 max-h-[65vh]">
        <label class="flex flex-col gap-1">
          <span class="text-[11px] font-semibold text-ink2">{{ $t('feedback.form.message') }}</span>
          <textarea
            v-model="message"
            rows="5"
            required
            :placeholder="$t('feedback.form.messagePlaceholder')"
            class="w-full resize-none rounded-md border border-border bg-[hsl(var(--muted)/0.4)] px-3 py-2 text-[13px] leading-snug placeholder:text-ink2 focus:outline-none focus:border-[hsl(var(--primary))]"
          />
        </label>

        <div>
          <span class="text-[11px] font-semibold text-ink2">{{ $t('feedback.form.images') }}</span>
          <div class="mt-1.5 flex flex-wrap gap-2">
            <div
              v-for="(preview, i) in imagePreviews"
              :key="preview.url"
              class="relative w-16 h-16 rounded-md overflow-hidden border border-border"
            >
              <img :src="preview.url" :alt="$t('feedback.form.images')" class="w-full h-full object-cover" />
              <button
                type="button"
                @click="removeImage(i)"
                class="absolute top-0.5 right-0.5 w-5 h-5 rounded-md bg-ink text-white flex items-center justify-center"
                :aria-label="$t('feedback.form.removeImage')"
              >
                <X class="w-2.5 h-2.5" />
              </button>
            </div>
            <button
              v-if="images.length < MAX_IMAGES"
              type="button"
              @click="fileInput?.click()"
              class="w-16 h-16 rounded-md border-2 border-dashed border-[hsl(var(--border))] flex items-center justify-center text-ink2 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
              :aria-label="$t('feedback.form.addImage')"
            >
              <ImagePlus class="w-5 h-5" />
            </button>
          </div>
          <input ref="fileInput" type="file" accept="image/*" multiple class="hidden" @change="onFilesChosen" />
          <p class="mt-1 text-[10.5px] text-ink2">
            {{ $t('feedback.form.imagesHint', { max: MAX_IMAGES, mb: MAX_IMAGE_MB }) }}
          </p>
          <p v-if="imageError" class="mt-1 text-[11px] text-[hsl(var(--danger))]">{{ imageError }}</p>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-[11px] font-semibold text-ink2">{{ $t('feedback.form.contactName') }}</span>
          <input
            v-model="contactName"
            type="text"
            autocomplete="name"
            :placeholder="$t('feedback.form.contactNamePlaceholder')"
            class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-[11px] font-semibold text-ink2">{{ $t('feedback.form.contactPhone') }}</span>
          <input
            :value="contactPhone"
            @input="contactPhone = normalizePhoneInput(($event.target as HTMLInputElement).value)"
            type="tel"
            autocomplete="tel"
            placeholder="+7 700 000 00 00"
            class="w-full h-10 px-3 rounded-md border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 transition"
            :class="contactPhoneInvalid
              ? 'border-[hsl(var(--danger))] focus:ring-[hsl(var(--danger)/0.3)] focus:border-[hsl(var(--danger))]'
              : 'border-[hsl(var(--border))] focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)]'"
          />
          <span v-if="contactPhoneInvalid" class="text-[11px] text-[hsl(var(--danger))]">{{ $t('routeRegistration.form.invalidPhone') }}</span>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-[11px] font-semibold text-ink2">{{ $t('feedback.form.contactEmail') }}</span>
          <input
            v-model="contactEmail"
            type="email"
            autocomplete="email"
            placeholder="mail@example.com"
            class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition"
          />
        </label>

        <label v-if="messengerIdentity" class="flex items-start gap-2 cursor-pointer">
          <input
            v-model="messengerConsent"
            type="checkbox"
            class="mt-0.5 w-4 h-4 shrink-0 rounded border-[hsl(var(--border))] text-[hsl(var(--primary))] focus:ring-[hsl(var(--primary)/0.3)]"
          />
          <span class="text-[12px] leading-snug text-[hsl(var(--foreground)/0.85)]">
            {{ $t('feedback.form.messengerConsent', { provider: messengerLabel }) }}
          </span>
        </label>
      </div>

      <div class="flex items-center gap-2 px-4 py-3 border-t border-[hsl(var(--border)/0.6)] shrink-0">
        <button
          type="button"
          @click="close"
          class="h-10 px-3 rounded-md bg-[hsl(var(--muted))] text-[12.5px] font-semibold hover:bg-[hsl(var(--muted)/0.8)] transition-colors"
        >{{ $t('routeRegistration.form.cancel') }}</button>
        <button
          type="button"
          @click="submit"
          :disabled="!canSubmit || submitting"
          class="flex-1 h-10 rounded-md bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          <Loader2 v-if="submitting" class="w-4 h-4 animate-spin" />
          {{ $t('feedback.form.submit') }}
        </button>
      </div>
    </div>

    <div
      v-if="showSuccess"
      ref="successPanel"
      role="dialog"
      aria-modal="true"
      class="fixed inset-0 z-[120] flex items-center justify-center bg-ink/50 px-4"
      @keydown="onDialogKeydown"
    >
      <div class="w-full max-w-xs bg-sheet rounded-xl shadow-ink p-5 text-center">
        <div class="w-11 h-11 mx-auto mb-3 rounded-full bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] flex items-center justify-center">
          <CheckCircle2 class="w-5 h-5" />
        </div>
        <p class="text-[14px] font-semibold mb-1.5">{{ $t('feedback.success.title') }}</p>
        <p class="text-[12.5px] text-ink2 leading-snug mb-4">{{ $t('feedback.success.message') }}</p>
        <button
          type="button"
          @click="dismissSuccess"
          class="w-full h-9 rounded-md bg-[hsl(var(--primary))] text-white text-[12.5px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors"
        >{{ $t('routeRegistration.terms.close') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDialogA11y } from "../composables/useDialogA11y";
import { computed, onBeforeUnmount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { X, ImagePlus, Loader2, CircleAlert, CheckCircle2 } from "lucide-vue-next";
import api from "../api";
import { useAuthStore } from "../store/auth";
import { useHistoryBackClose } from "../composables/useHistoryBackClose";
import { normalizePhoneInput, isValidKzPhone } from "../utils/phone";
import type { FeedbackSubmitResponse } from "../types/feedback";

const props = defineProps<{
  source: string;
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { t } = useI18n();
const auth = useAuthStore();

const MAX_IMAGES = 5;
const MAX_IMAGE_MB = 10;

const MESSENGER_LABELS: Record<string, string> = {
  telegram: "Telegram",
};
const messengerIdentity = computed(() => auth.identities.find((i) => i.provider === "telegram"));
const messengerLabel = computed(() => MESSENGER_LABELS[messengerIdentity.value?.provider ?? ""] ?? messengerIdentity.value?.provider ?? "");

const message = ref("");
const images = ref<File[]>([]);
const imagePreviews = ref<{ url: string }[]>([]);
const imageError = ref("");
const fileInput = ref<HTMLInputElement | null>(null);

const contactName = ref(auth.displayName || [auth.firstName, auth.lastName].filter(Boolean).join(" "));
const contactPhone = ref("");
const contactEmail = ref(auth.email || "");
const messengerConsent = ref(false);

const submitting = ref(false);
const error = ref("");
const showSuccess = ref(false);

const contactPhoneInvalid = computed(() => !!contactPhone.value.trim() && !isValidKzPhone(contactPhone.value));
const canSubmit = computed(() => !!message.value.trim() && !contactPhoneInvalid.value);

const onFilesChosen = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const chosen = Array.from(input.files ?? []);
  input.value = "";
  imageError.value = "";

  const room = MAX_IMAGES - images.value.length;
  if (chosen.length > room) {
    imageError.value = t("feedback.form.tooManyImages", { max: MAX_IMAGES });
  }
  for (const file of chosen.slice(0, room)) {
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      imageError.value = t("feedback.form.imageTooLarge", { name: file.name, mb: MAX_IMAGE_MB });
      continue;
    }
    images.value.push(file);
    imagePreviews.value.push({ url: URL.createObjectURL(file) });
  }
};

const removeImage = (index: number) => {
  URL.revokeObjectURL(imagePreviews.value[index].url);
  images.value.splice(index, 1);
  imagePreviews.value.splice(index, 1);
};

const revokeAllPreviews = () => {
  imagePreviews.value.forEach((p) => URL.revokeObjectURL(p.url));
};
onBeforeUnmount(revokeAllPreviews);

const close = () => {
  revokeAllPreviews();
  emit("close");
};

// Кнопка "назад" в браузере закрывает форму, а не листает страницу под ней.
useHistoryBackClose(close);

const submit = async () => {
  if (!canSubmit.value || submitting.value) return;
  submitting.value = true;
  error.value = "";
  try {
    const form = new FormData();
    form.append("source", props.source);
    form.append("message", message.value.trim());
    if (contactName.value.trim()) form.append("contact_name", contactName.value.trim());
    if (contactPhone.value.trim()) form.append("contact_phone", contactPhone.value.trim());
    if (contactEmail.value.trim()) form.append("contact_email", contactEmail.value.trim());
    if (messengerIdentity.value) {
      form.append("messenger_consent", String(messengerConsent.value));
      form.append("messenger_provider", messengerIdentity.value.provider);
    }
    for (const file of images.value) form.append("files", file);

    await api.post<FeedbackSubmitResponse>("/feedback", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    showSuccess.value = true;
  } catch {
    error.value = t("feedback.form.errorGeneric");
  } finally {
    submitting.value = false;
  }
};

const dismissSuccess = () => {
  showSuccess.value = false;
  close();
};

// Доступность окна: ловушка фокуса, Esc, возврат фокуса (useDialogA11y).
const dialogPanel = ref<HTMLElement | null>(null);
// Экран успеха - отдельный оверлей рядом с панелью: пока он открыт, фокус
// и Esc принадлежат ему (Esc закрывает его так же, как кнопка «Понятно»).
const successPanel = ref<HTMLElement | null>(null);
const { titleId: dialogTitleId, onKeydown: onDialogKeydown } = useDialogA11y(dialogPanel, {
  onClose: () => (showSuccess.value ? dismissSuccess() : close()),
  activeRoot: () => (showSuccess.value ? successPanel.value : null),
});
</script>
